import { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { FormikProvider, useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { Claiming } from "@prisma/client";

import AfterDecentralandRedeemModal from "../AfterDecentralandRedeemModal";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { decentralandFormValidator } from "validators/client/redeemForm";

import {
  selectAddress,
  selectUserEmail,
  selectUserNickname,
} from "redux/slices/metamask";

import OrderCosts from "sections/cartPage/OrderCosts";
import DecentralandRedeemedInfo from "sections/itemsPage/DecentralandRedeemedInfo";

import CustomOutlinedInput from "components/CustomOutlinedInput";
import InfoBox from "components/InfoBox";
import { INFO_BOX_TYPE } from "components/InfoBox/types";

import { PURPOSES_TYPES } from "enums/purposesTypes";
import { PRODUCTION_STATUSES } from "enums/productionStatuses";
import { PRODUCT_TYPES } from "enums/productTypes";
import { CART_ITEM_TYPES } from "enums/cartItemTypes";

const initialState = {
  nickname: "",
  email: "",
  termsChecked: false,
  personalDataChecked: false,
  marketingChecked: false,
};

export interface FormValues {
  nickname: string;
  email: string;
  termsChecked: boolean;
  personalDataChecked: boolean;
  marketingChecked: boolean;
}

type SectionClaimingDecentralandProps = {
  contractAddress: string;
  tokenId: number;
  itemName: string;
  productType: PRODUCT_TYPES | null;
  decentralandClaiming?: Pick<
    Claiming,
    | "updatedAt"
    | "transactionHash"
    | "chainId"
    | "dclContractAddress"
    | "dclTokenId"
    | "productionStatus"
  > | null;
  isOwner: boolean;
  isCustodialOwner?: boolean;
  isRedeemedAlready?: boolean;
};

const SectionClaimingDecentraland: FC<SectionClaimingDecentralandProps> = ({
  contractAddress,
  tokenId,
  itemName,
  productType,
  decentralandClaiming,
  isRedeemedAlready,
  isOwner,
  isCustodialOwner,
}) => {
  const [isAfterRedeemModalOpen, setAfterRedeemModalOpen] = useState(false);

  const metamaskAddress = useSelector(selectAddress);
  const userEmail = useSelector(selectUserEmail);
  const userNickname = useSelector(selectUserNickname);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      ...initialState,
      email: userEmail || "",
      nickname: userNickname || "",
    },
    validationSchema: toFormikValidationSchema(decentralandFormValidator),
    validateOnChange: false,
    onSubmit: (values) => {
      console.info("Form Submitted", values);
    },
  });

  const items = [
    {
      tokenId,
      contractAddress,
      type: CART_ITEM_TYPES.REDEEM as const,
      cartId: `${contractAddress}_${tokenId}`,
      amount: 1,
      productType,
    },
  ];

  const handleRedeemed = useCallback(() => {
    setAfterRedeemModalOpen(true);
  }, []);

  const isRedeemable = !isRedeemedAlready && isOwner;

  if (isRedeemedAlready && decentralandClaiming) {
    return (
      <Box component="section">
        <DecentralandRedeemedInfo
          dclContractAddress={decentralandClaiming.dclContractAddress}
          dclTokenId={decentralandClaiming.dclTokenId}
          productionStatus={
            decentralandClaiming.productionStatus as PRODUCTION_STATUSES
          }
        />
      </Box>
    );
  }

  return (
    <Box component="form">
      <Typography paragraph>
        The item is available in the Decentraland Metaverse. Press the button to
        proceed further.
      </Typography>
      <FormikProvider value={formik}>
        <Box mt={2}>
          <CustomOutlinedInput
            label="Nickname*"
            inputProps={{
              id: "nickname",
              placeholder: "Nickname*",
              value: formik.values.nickname,
              onChange: formik.handleChange,
            }}
            formControlProps={{
              fullWidth: true,
              margin: "normal",
            }}
            error={formik.errors.nickname}
          />
        </Box>
        <Box>
          <CustomOutlinedInput
            label="Email*"
            description={
              "Enter the email address in case we need to contact you."
            }
            inputProps={{
              id: "email",
              placeholder: "Email*",
              value: formik.values.email,
              onChange: formik.handleChange,
            }}
            formControlProps={{
              fullWidth: true,
              margin: "normal",
            }}
            error={formik.errors.email}
          />
        </Box>

        <OrderCosts
          items={items}
          purpose={PURPOSES_TYPES.DECENTRALAND}
          isRedeemable={isRedeemable && isOwner}
          onAfterRedeem={handleRedeemed}
        />

        {metamaskAddress && isCustodialOwner && (
          <InfoBox type={INFO_BOX_TYPE.REJECTED}>
            Before redeem, you need to transfer the item to the Metamask address
            from the custodial wallet.
          </InfoBox>
        )}

        {isAfterRedeemModalOpen && (
          <AfterDecentralandRedeemModal
            isOpen={isAfterRedeemModalOpen}
            itemName={itemName}
          />
        )}
      </FormikProvider>
    </Box>
  );
};

export default SectionClaimingDecentraland;
